import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ArrowBack } from "@mui/icons-material";
import { Formik, Form } from "formik";
import BusinessInformationForm from "./forms/BusinessInformationForm";
import PrimaryInformationForm from "./forms/PrimaryInformationForm";
import BusinessDetailsForm from "./forms/BusinessDetailsForm";
import { Checkbox } from "@mui/material";
import { registerPartner } from "../../../../api/become-partner";
import PlannerSuccessModal from "../../../../common/components/planner-success-modal/PlannerSuccessModal";
import {
  initialValues,
  validationSchema,
} from "./partner-registration-form.utils";
import { getCityCoordinates } from "../../../../api/mapbox";
import ToastMessage from "../../../../common/components/toast-message/toast-message";
import { routes } from "../../../../constants/routes";
import { AFP_HORIZON_PRIVACY_POLICY_LINK } from "../../../../utils/constants";
import { useNavigate } from "react-router-dom";
import "./partner-registration-form.scss";

const PartnerRegistrationForm: React.FC = () => {
  const [showSuccess, setShowSuccess] = React.useState<boolean>(false);
  const businessNameRef = React.useRef<HTMLInputElement>(null!);
  const businessEmailRef = React.useRef<HTMLInputElement>(null!);
  const businessPhoneRef = React.useRef<HTMLInputElement>(null!);
  const businessRegistrationNumberRef = React.useRef<HTMLInputElement>(null!);
  const [showToast, setShowToast] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
    close: () => void;
  }>({ open: false, message: "", severity: "info", close: () => { } });
  const navigate = useNavigate();

  const handleBack = () => {
    navigate("/become-provider");
  };

  const handleSubmit = async (values: any, formik: any) => {
    let latitude = 0;
    let longitude = 0;
    try {
      const response = await getCityCoordinates(values.businessCity);
      if (response.data?.features?.length > 0) {
        const { center } = response.data.features[0];
        longitude = center[0];
        latitude = center[1];
      }
    } catch (error) {
      console.error("Error fetching city coordinates:", error);
    }
    const formattedValues = {
      businessName: values.businessName,
      businessType: values.businessType,
      phoneNumber: values.businessPhoneCC + values.businessPhone,
      email: values.businessEmail,
      website: values.businessWebsite,
      contactName: values.primaryContactName,
      contactEmail: values.primaryContactEmail,
      contactPhone: values.primaryContactPhoneCC + values.primaryContactPhone,
      contactPosition: values.primaryContactRole,
      yearsInBusiness: Number(values.yearsinOperation),
      agreedToTerms: values.agreedToTerms,
      ...(values.operationHours?.trim()
        ? { operationalHours: values.operationHours }
        : {}),
      numberOfEmployees: Number(values.staffStrength),
      ...(values.serviceDescription
        ? { businessDescription: values.serviceDescription }
        : {}),
      address: {
        street_line_1: values.businessAddress,
        city: values.businessCity,
        state: values.businessState,
        zipcode: values.businessZipCode,
        country: values.businessCountry,
      },
      needTypes: values.needTypes,
      serviceCategories: values.serviceCategories,
      ...(values.emergencyContactPhone && values.isEmergencyServiceAvailable
        ? {
          emergencyContactPhone:
            values.emergencyContactPhoneCC + values.emergencyContactPhone,
        }
        : {}),
      serviceCoverageArea: values.serviceCoverageArea,
      isEmergencyServiceAvailable: values.isEmergencyServiceAvailable,
      latitude,
      longitude,
      ...(values.businessRegistrationNumber
        ? { licenseId: values.businessRegistrationNumber }
        : { licenseId: null }),
    };
    const appendFormData = (fd: FormData, data: any, parentKey?: string) => {
      if (data === null || data === undefined) return;
      if (Array.isArray(data)) {
        data.forEach((val, index) => {
          const key = parentKey ? `${parentKey}[${index}]` : `[${index}]`;
          if (val instanceof Blob) {
            fd.append(key, val);
          } else if (typeof val === "object" && val !== null) {
            appendFormData(fd, val, key);
          } else {
            fd.append(key, String(val));
          }
        });
        return;
      }
      if (typeof data === "object" && !(data instanceof Blob)) {
        Object.entries(data).forEach(([k, v]) => {
          const composedKey = parentKey ? `${parentKey}[${k}]` : k;
          appendFormData(fd, v as any, composedKey);
        });
        return;
      }
      if (!parentKey) return;
      fd.append(parentKey, String(data));
    };
    const formData = new FormData();
    appendFormData(formData, formattedValues);
    if (values.licenseDocument) {
      formData.append("licence_document", values.licenseDocument);
    }
    if (values.pricingDocument) {
      formData.append("package_document", values.pricingDocument);
    }
    if (values.serviceFiles && values.serviceFiles.length > 0) {
      values.serviceFiles.forEach((file: File) => {
        formData.append("service_certificates", file);
      });
    }
    try {
      const response = await registerPartner(formData);
      if (response?.data.success) {
        setTimeout(() => {
          setShowSuccess(true);
          formik.resetForm();
        }, 500);
      }
    } catch (error) {
      console.error("Error:", error);
      const message =
        (error as any)?.response?.data?.message || "Something went wrong";
      setShowToast({
        open: true,
        message,
        severity: "error",
        close: () => { },
      });
      const errorCode = (error as any)?.response?.data?.errorCode;
      if (errorCode === "PROVIDER_EXISTS") {
        formik.setFieldError(
          "businessEmail",
          "This email is already registered"
        );
        businessEmailRef.current?.focus();
        businessEmailRef.current?.scrollIntoView({ behavior: "smooth" });
      }
      if (errorCode === "PARTNER_PHONE_NUMBER_EXISTS") {
        formik.setFieldError(
          "businessPhone",
          "This phone number is already registered"
        );
        businessPhoneRef.current?.focus();
        businessPhoneRef.current?.scrollIntoView({ behavior: "smooth" });
      }
      if (errorCode === "LICENSE_ID_EXISTS") {
        formik.setFieldError(
          "businessRegistrationNumber",
          "This license number is already registered"
        );
        businessRegistrationNumberRef.current?.focus();
        businessRegistrationNumberRef.current?.scrollIntoView({
          behavior: "smooth",
        });
      }
      if (errorCode === "BUSINESS_NAME_EXISTS") {
        formik.setFieldError(
          "businessName",
          "This business name is already registered"
        );
        businessNameRef.current?.focus();
        businessNameRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <Box
      sx={{
        margin: "20px 10px",
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        padding: "20px",
        borderRadius: "8px",
        fontFamily: "Roboto",
      }}
      className="partner-registration-form"
    >
      <Box
        mb={4}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Grid container alignItems="center">
          <Grid item xs={4}>
            <Button
              startIcon={<ArrowBack fontSize="large" />}
              onClick={handleBack}
              disableRipple
              sx={{
                textTransform: "none",
                color: "#161616",
                paddingTop: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
                cursor: "pointer",
              }}
            >
              Back
            </Button>
          </Grid>

          <Grid item xs={4} textAlign="center">
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontSize: "24px",
                fontWeight: "600",
                lineHeight: "43px",
                letterSpacing: "-1%",
                color: "#161616",
              }}
            >
              Fill the details
            </Typography>
          </Grid>

          {/*  Spacer */}
          <Grid item xs={4} />
        </Grid>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{
            fontSize: "20px",
            fontWeight: "400",
            lineHeight: "37px",
            textAlign: "center",
            color: "#161616",
          }}
        >
          Join our Network of trusted death care service providers and grow your
          business
        </Typography>
      </Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          return (
            <Form>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
              >
                <Accordion
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #CED4DA",
                    borderRadius: "10px",
                    "& .MuiAccordionSummary-root.Mui-expanded": {
                      borderBottom: "1px solid #CED4DA",
                    },
                  }}
                  defaultExpanded
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Badge
                        sx={{
                          background: "#104145",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "16px",
                          height: "30px",
                          width: "30px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        1
                      </Badge>
                      Business Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <BusinessInformationForm
                      formik={formik}
                      businessNameRef={businessNameRef}
                      businessEmailRef={businessEmailRef}
                      businessPhoneRef={businessPhoneRef}
                      businessRegistrationNumberRef={
                        businessRegistrationNumberRef
                      }
                    />
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #CED4DA",
                    borderRadius: "10px",
                    "& .MuiAccordionSummary-root.Mui-expanded": {
                      borderBottom: "1px solid #CED4DA",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                  >
                    <Typography
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Badge
                        sx={{
                          background: "#104145",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "16px",
                          height: "30px",
                          width: "30px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        2
                      </Badge>
                      Primary Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PrimaryInformationForm formik={formik} />
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #CED4DA",
                    borderRadius: "10px",
                    "& .MuiAccordionSummary-root.Mui-expanded": {
                      borderBottom: "1px solid #CED4DA",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                  >
                    <Typography
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Badge
                        sx={{
                          background: "#104145",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "16px",
                          height: "30px",
                          width: "30px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        3
                      </Badge>
                      Business Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <BusinessDetailsForm formik={formik} />
                  </AccordionDetails>
                </Accordion>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  mt: 2,
                  fontFamily: "Inter",
                  gap: 5,
                  background: "#FFFFFF",
                  borderRadius: "8px",
                  padding: "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1,
                    width: "50%",
                  }}
                >
                  <Checkbox
                    sx={{
                      color: "#104145",
                      verticalAlign: "middle",
                      mr: 1,
                      mt: 1,
                      borderRadius: "2px",
                      height: "18px",
                      width: "18px",
                      "&.Mui-checked": {
                        color: "#104451",
                      },
                    }}
                    checked={formik.values.agreedToTerms}
                    onChange={async (e) => {
                      const checked = e.target.checked;

                      await formik.setFieldValue("agreedToTerms", checked);
                      formik.setFieldTouched("agreedToTerms", true, true);

                      // Force validation
                      await formik.validateField("agreedToTerms");
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        fontSize: "14px",
                        lineHeight: "160%",
                        color: "#4B4F50",
                        marginBottom: "8px",
                        fontWeight: "400",
                        letterSpacing: "-0.6%",
                        verticalAlign: "middle",
                      }}
                    >
                      I agree{" "}
                      <a
                        href="/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#BFA755",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        terms and conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href={AFP_HORIZON_PRIVACY_POLICY_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#BFA755",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        Privacy Policy
                      </a>{" "}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "14px",
                        color: "#4B4F50",
                        fontWeight: "400",
                        lineHeight: "20px",
                        fontFamily: "Roboto",
                      }}
                    >
                      By submitting this form, you agree to allow MemorialGo to
                      verify your business information. We may contact you for
                      additional documentation.
                    </Typography>
                  </Box>
                </Box>
                {formik.touched.agreedToTerms &&
                  formik.errors.agreedToTerms && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ fontSize: "12px" }}
                    >
                      {formik.errors.agreedToTerms}
                    </Typography>
                  )}

                <Box
                  className="form-buttons"
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => formik.resetForm()}
                    sx={{
                      color: "#104145",
                      borderColor: "#000",
                      textTransform: "none",
                      mr: 2,
                      width: "150px",
                      height: "40px",
                      borderRadius: "4px",
                      fontWeight: "400",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      !formik.isValid ||
                      formik.isSubmitting ||
                      !formik.values.agreedToTerms
                    }
                    sx={{
                      color: "#104145",
                      borderColor: "#000",
                      textTransform: "none",
                      mr: 2,
                      width: "150px",
                      height: "40px",
                      borderRadius: "4px",
                      fontWeight: "400",
                      fontSize: "14px",
                      backgroundColor: "#FFEDBE",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
      {showSuccess && (
        <PlannerSuccessModal
          open={showSuccess}
          onClose={() => {
            setShowSuccess(false);
            navigate(routes.BECOME_PROVIDER);
          }}
          title="Submission Successful"
          subtitle=""
          businessName={""}
          infoText="Thank you for registering with us. Your registration is currently under review, and our team will get back to you shortly."
          isConsultant={false}
        />
      )}
      {showToast.open && (
        <ToastMessage
          open={showToast.open}
          message={showToast.message}
          severity={showToast.severity}
          onClose={() => {
            setShowToast({ ...showToast, open: false });
          }}
        />
      )}
    </Box>
  );
};

export default PartnerRegistrationForm;
